import React, { useState, useEffect, useMemo } from 'react';
import { 
    Box, AppBar, Toolbar, Typography, Button, Container, Stack, 
    CssBaseline, Snackbar, Alert, IconButton, Drawer, List, useMediaQuery,
    ListItem, ListItemButton, ListItemIcon, ListItemText, Divider, Tooltip 
} from '@mui/material';
import { BrowserRouter, Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';
import SignUp from './components/SignUp';
import ExternalTicketForm from './components/ExternalTicketForm';
import ExternalTicketFollowUp from './components/ExternalTicketFollowUp';
import Profile from './components/Profile';
import TicketReception from './components/TicketReception';
import UserManagement from './components/UserManagement';
import TicketDetail from './components/TicketDetail';
import Configuration from './components/Configuration';
import NotFound from './components/NotFound';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import { userService, authService } from './api';
import './App.css';

const drawerWidth = 260;

// Componente para proteger rutas que requieren autenticación
const ProtectedRoute = ({ children, isAuthenticated }) => {
  const location = useLocation();
  
  if (!isAuthenticated) {
    // Si la sesión expiró (había un token pero ya no), podemos pasar un flag extra
    const wasAuthenticated = !!localStorage.getItem('had_session');
    return <Navigate 
      to="/login" 
      state={{ from: location, expired: wasAuthenticated }} 
      replace />;
  }
  return children;
};

function AppContent({ toggleMode, mode }) {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token'));
  const [userRole, setUserRole] = useState(localStorage.getItem('role'));
  const [openDenied, setOpenDenied] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md')); // Detecta pantallas pequeñas
  const [mobileOpen, setMobileOpen] = useState(false); // Estado para abrir/cerrar el drawer en móvil
  const [isCollapsed, setIsCollapsed] = useState(false); // Estado para colapsar el drawer en desktop
  
  // Detecta si la pantalla está en el rango "medio" (entre 900px y 1200px)
  const isTabletRange = useMediaQuery((theme) => theme.breakpoints.between('md', 'lg'));

  // Efecto para colapsar automáticamente en tamaño medio
  useEffect(() => {
    if (isTabletRange) {
      setIsCollapsed(true);
    } else if (!isMobile) {
      setIsCollapsed(false);
    }
  }, [isTabletRange, isMobile]);

  // Efecto para detectar si venimos de una redirección por falta de permisos
  useEffect(() => {
    if (location.state?.denied) {
      setOpenDenied(true);
      // Limpiamos el estado para que la alerta no reaparezca al refrescar
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  // Correctiva Punto 3: Sincronizar y validar el rol con el servidor
  useEffect(() => {
    const verifySession = async () => {
      if (isAuthenticated) {
        try {
          const res = await userService.getProfile();
          const serverRole = res.data.role;
          // Si el rol en el servidor es distinto al guardado localmente, actualizamos
          if (serverRole !== userRole) {
            setUserRole(serverRole);
            localStorage.setItem('role', serverRole);
          }
        } catch (err) {
          if (err.response?.status === 401) handleLogout();
        }
      }
    };
    verifySession();
  }, [isAuthenticated]); // Se ejecuta al cargar y cuando cambia la autenticación

  const handleLoginSuccess = (role) => {
    localStorage.setItem('role', role);
    setIsAuthenticated(true);
    setUserRole(role);
    // Marcador para saber que el usuario tuvo una sesión activa
    localStorage.setItem('had_session', 'true');

    // Intentamos recuperar la ruta previa desde el estado de la ubicación
    const origin = location.state?.from?.pathname || '/dashboard';
    // Redirigimos al origen y usamos replace: true para que no se pueda volver al login con "atrás"
    navigate(origin, { replace: true });
  };

  const handleLogout = async () => {
    try { await authService.logout(); } catch (e) { console.error(e); }
    localStorage.removeItem('token');
    localStorage.removeItem('role');
    localStorage.removeItem('had_session');
    setIsAuthenticated(false);
    setUserRole(null);
    navigate('/login');
  };

  const toggleLanguage = () => {
    const newLang = i18n.language.startsWith('en') ? 'es' : 'en';
    i18n.changeLanguage(newLang);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
      <Box sx={{ display: 'flex' }}>
        <AppBar 
          position="fixed" 
          elevation={0} 
          sx={{ 
            zIndex: (theme) => theme.zIndex.drawer + 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            color: 'text.primary',
          }}
        >
          <Toolbar>
            {isMobile && isAuthenticated && (
              <IconButton color="inherit" aria-label="open drawer" edge="start" onClick={handleDrawerToggle} sx={{ mr: 2 }}>
                <MenuIcon />
              </IconButton>
            )}
            {!isMobile && isAuthenticated && (
              <IconButton onClick={toggleCollapse} sx={{ mr: 2 }}>
                {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            )}
            <Box 
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', mr: 2 }}
              onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
            >
              <img 
                src="/logo.svg" 
                alt="TicSmart Logo" 
                style={{ height: '40px', maxWidth: '180px', objectFit: 'contain' }} 
              />
            </Box>
            <Box sx={{ flexGrow: 1 }} />
            <Stack direction="row" spacing={1} alignItems="center">
              {isAuthenticated ? (
                <Button color="error" size="small" startIcon={<LogoutIcon />} onClick={handleLogout}>Cerrar Sesión</Button>
              ) : null}
              <IconButton onClick={toggleMode} color="inherit">
                {mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
              </IconButton>
              <Button color="secondary" variant="contained" size="small" onClick={toggleLanguage} sx={{ ml: 2 }}>
                {i18n.language.toUpperCase()}
              </Button>
            </Stack>
          </Toolbar>
        </AppBar>

        {isAuthenticated && (
          <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          sx={{
            width: isMobile ? drawerWidth : (isCollapsed ? 72 : drawerWidth),
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: { 
              width: isMobile ? drawerWidth : (isCollapsed ? 72 : drawerWidth), 
              boxSizing: 'border-box', 
              borderRight: '1px solid', 
              borderColor: 'divider',
              transition: (theme) => theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden'
            },
          }}
        >
          <Toolbar /> {/* Espaciador para que el contenido no quede bajo el AppBar */}
          <Box sx={{ overflow: 'auto', mt: 2 }}>
            <List>
              {!isCollapsed && <Typography variant="caption" sx={{ px: 3, fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase' }}>Gestión</Typography>}
              <Tooltip title={isCollapsed ? t('nav_overview') : ""} placement="right">
                <ListItemButton 
                  sx={{ 
                    mt: 1, 
                    justifyContent: isCollapsed ? 'center' : 'initial',
                    px: isCollapsed ? 0 : 2.5
                  }} 
                  component={Link} 
                  to="/dashboard" 
                  selected={location.pathname === '/dashboard'}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                    <DashboardIcon />
                  </ListItemIcon>
                  {!isCollapsed && <ListItemText primary={t('nav_overview')} />}
                </ListItemButton>
              </Tooltip>

              {(userRole === 'ADMIN' || userRole === 'AGENT') && (
                <Tooltip title={isCollapsed ? t('nav_tickets') : ""} placement="right">
                  <ListItemButton 
                    sx={{ justifyContent: isCollapsed ? 'center' : 'initial', px: isCollapsed ? 0 : 2.5 }}
                    component={Link} 
                    to="/tickets" 
                    selected={location.pathname.startsWith('/tickets')}
                  >
                    <ListItemIcon sx={{ minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                      <ConfirmationNumberIcon />
                    </ListItemIcon>
                    {!isCollapsed && <ListItemText primary={t('nav_tickets')} />}
                  </ListItemButton>
                </Tooltip>
              )}

              <Divider sx={{ my: 2 }} />
              {!isCollapsed && <Typography variant="caption" sx={{ px: 3, fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase' }}>Portal de Soporte</Typography>}

              <Tooltip title={isCollapsed ? t('nav_submit') : ""} placement="right">
                <ListItemButton 
                  sx={{ mt: 1, justifyContent: isCollapsed ? 'center' : 'initial', px: isCollapsed ? 0 : 2.5 }}
                  component={Link} 
                  to="/external-ticket" 
                  selected={location.pathname === '/external-ticket'}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                    <AddCircleOutlineIcon />
                  </ListItemIcon>
                  {!isCollapsed && <ListItemText primary={t('nav_submit')} />}
                </ListItemButton>
              </Tooltip>

              <Tooltip title={isCollapsed ? t('nav_follow') : ""} placement="right">
                <ListItemButton 
                  sx={{ justifyContent: isCollapsed ? 'center' : 'initial', px: isCollapsed ? 0 : 2.5 }}
                  component={Link} 
                  to="/external-followup" 
                  selected={location.pathname === '/external-followup'}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                    <SearchIcon />
                  </ListItemIcon>
                  {!isCollapsed && <ListItemText primary={t('nav_follow')} />}
                </ListItemButton>
              </Tooltip>

              {userRole === 'ADMIN' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  {!isCollapsed && <Typography variant="caption" sx={{ px: 3, fontWeight: 'bold', color: 'text.secondary', textTransform: 'uppercase' }}>Administración</Typography>}
                  <Tooltip title={isCollapsed ? t('nav_users') : ""} placement="right">
                    <ListItemButton 
                      sx={{ mt: 1, justifyContent: isCollapsed ? 'center' : 'initial', px: isCollapsed ? 0 : 2.5 }}
                      component={Link} 
                      to="/usuarios" 
                      selected={location.pathname === '/usuarios'}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                        <PeopleIcon />
                      </ListItemIcon>
                      {!isCollapsed && <ListItemText primary={t('nav_users')} />}
                    </ListItemButton>
                  </Tooltip>
                  <Tooltip title={isCollapsed ? t('nav_config') : ""} placement="right">
                    <ListItemButton 
                      sx={{ justifyContent: isCollapsed ? 'center' : 'initial', px: isCollapsed ? 0 : 2.5 }}
                      component={Link} 
                      to="/configuracion" 
                      selected={location.pathname === '/configuracion'}
                    >
                      <ListItemIcon sx={{ minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                        <SettingsIcon />
                      </ListItemIcon>
                      {!isCollapsed && <ListItemText primary={t('nav_config')} />}
                    </ListItemButton>
                </Tooltip>
                </>
              )}

              <Divider sx={{ my: 2 }} />
              <Tooltip title={isCollapsed ? t('nav_profile') : ""} placement="right">
                <ListItemButton 
                  sx={{ justifyContent: isCollapsed ? 'center' : 'initial', px: isCollapsed ? 0 : 2.5 }}
                  component={Link} 
                  to="/perfil" 
                  selected={location.pathname === '/perfil'}
                >
                  <ListItemIcon sx={{ minWidth: 0, mr: isCollapsed ? 0 : 2, justifyContent: 'center' }}>
                    <AccountBoxIcon />
                  </ListItemIcon>
                  {!isCollapsed && <ListItemText primary={t('nav_profile')} />}
                </ListItemButton>
              </Tooltip>
            </List>
          </Box>
          </Drawer>
        )}

        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: 3, 
            width: (isMobile || !isAuthenticated) ? '100%' : `calc(100% - ${isCollapsed ? 72 : drawerWidth}px)`,
            transition: (theme) => theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
          }}
        >
          <Toolbar /> {/* Espaciador para el contenido principal */}
          <Routes>
            {/* Redirigir la raíz a login */}
            <Route path="/" element={<Navigate to="/login" />} />
            {/* Rutas públicas accesibles sin autenticación */}
            <Route path="/login" element={<Auth onLoginSuccess={handleLoginSuccess} />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />

            {/* Rutas Protegidas */}
            <Route path="/external-ticket" element={<ProtectedRoute isAuthenticated={isAuthenticated}><ExternalTicketForm /></ProtectedRoute>} />
            <Route path="/external-followup" element={<ProtectedRoute isAuthenticated={isAuthenticated}><ExternalTicketFollowUp /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute isAuthenticated={isAuthenticated}><Dashboard /></ProtectedRoute>} />
            <Route path="/tickets" element={<ProtectedRoute isAuthenticated={isAuthenticated}><TicketReception /></ProtectedRoute>} />
            <Route path="/tickets/:id" element={<ProtectedRoute isAuthenticated={isAuthenticated}><TicketDetail /></ProtectedRoute>} />
            <Route path="/perfil" element={<ProtectedRoute isAuthenticated={isAuthenticated}><Profile /></ProtectedRoute>} />
            <Route 
              path="/usuarios" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  {userRole === 'ADMIN' ? <UserManagement /> : <Navigate to="/dashboard" state={{ denied: true }} />}
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/configuracion" 
              element={
                <ProtectedRoute isAuthenticated={isAuthenticated}>
                  {userRole === 'ADMIN' ? <Configuration /> : <Navigate to="/dashboard" state={{ denied: true }} />}
                </ProtectedRoute>
              }
            />

            {/* Catch-all para 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Box>

        {/* Componente de Alerta Global */}
        <Snackbar 
          open={openDenied} 
          autoHideDuration={5000} 
          onClose={() => setOpenDenied(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setOpenDenied(false)} severity="error" variant="filled" sx={{ width: '100%' }}>
            No tienes permisos para acceder a esta sección.
          </Alert>
        </Snackbar>
      </Box>
  );
}

function App() {
  const [mode, setMode] = useState(localStorage.getItem('themeMode') || 'light');

  const theme = useMemo(() => createTheme({
    typography: {
      fontFamily: "'Open Sans', sans-serif",
      h4: {
        fontWeight: 700,
      },
      button: {
        textTransform: "none",
        fontWeight: 600,
      },
    },
    palette: {
      mode,
      primary: {
        main: mode === 'light' ? "#742DEF" : "#b388ff", // Versión suavizada para modo oscuro
        light: mode === 'light' ? "#4791db" : "#bb91ff",
        dark: mode === 'light' ? "#115293" : "#805ad5",
      },
      secondary: {
        main: "#FF8C19",
        light: "#FF8C19",
        dark: "#ff8b00",
      },
      background: {
        default: mode === 'light' ? "#f4f7f9" : "#121212",
        paper: mode === 'light' ? "#ffffff" : "#1e1e1e",
      },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            boxShadow: mode === 'light' 
              ? '0px 12px 40px rgba(0, 0, 0, 0.04)' // Sombra muy suave y esparcida para modo claro
              : '0px 12px 40px rgba(0, 0, 0, 0.3)', // Un poco más densa para que se note en fondos oscuros
            backgroundImage: 'none', // Limpiamos gradientes por defecto de MUI en modo oscuro
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === 'light' ? '#2c2c2c' : '#e6e1e5', // Fondo oscuro en light, claro en dark para contraste M3
            color: mode === 'light' ? '#ffffff' : '#313033',
            borderRadius: 8, // Bordes más redondeados tipo M3
            fontSize: '0.75rem',
            padding: '8px 12px',
            fontWeight: 500,
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
          },
          arrow: {
            color: mode === 'light' ? '#2c2c2c' : '#e6e1e5',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 24,
            padding: "8px 24px",
            boxShadow: "none",
            "&:hover": {
              boxShadow: "0px 1px 3px rgba(0,0,0,0.2)",
            },
          },
          sizeSmall: {
            padding: "4px 16px",
            fontSize: "0.8125rem",
          },
          sizeLarge: {
            padding: "12px 32px",
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 32, // Forma de píldora
            margin: '4px 12px', // Espaciado lateral para que flote
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
            },
            '&.Mui-selected': {
              backgroundColor: mode === 'light' ? 'rgba(116, 45, 239, 0.12)' : 'rgba(179, 136, 255, 0.16)',
              color: mode === 'light' ? '#742DEF' : '#b388ff',
              '& .MuiListItemIcon-root': {
                color: mode === 'light' ? '#742DEF' : '#b388ff',
              },
              '&:hover': {
                backgroundColor: mode === 'light' ? 'rgba(116, 45, 239, 0.18)' : 'rgba(179, 136, 255, 0.24)',
              },
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 0, // Permite centrar el icono sin el offset de 56px por defecto
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontWeight: 500, // Texto un poco más grueso para M3
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderWidth: "2px",
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: "12px 12px 0 0",
            "&:before": {
              display: "none",
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: "0.9rem",
          },
        },
      },
    },
  }), [mode]);

  const toggleMode = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    localStorage.setItem('themeMode', newMode);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AppContent toggleMode={toggleMode} mode={mode} />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;